import { Api } from "@api";
import { State } from "../";
import { ActionType } from "../types";
import { ThunkAction } from "redux-thunk";
import { UserDelegate, UserDelegatesAction } from "../types/user_delegates";

function deleteUserDelegateRequest(
  id: UserDelegate["id"]
): UserDelegatesAction {
  return {
    type: ActionType.DELETE_USER_DELEGATE_REQUEST,
    id,
  };
}

function deleteUserDelegateSuccess(
  id: UserDelegate["id"],
  success: boolean
): UserDelegatesAction {
  return {
    type: ActionType.DELETE_USER_DELEGATE_SUCCESS,
    id,
    success,
  };
}

function deleteUserDelegateFailure(
  id: UserDelegate["id"],
  error: Error
): UserDelegatesAction {
  return {
    type: ActionType.DELETE_USER_DELEGATE_FAILURE,
    id,
    error,
  };
}

export function deleteUserDelegate(
  id: UserDelegate["id"]
): ThunkAction<void, State, unknown, UserDelegatesAction> {
  return async (dispatch) => {
    dispatch(deleteUserDelegateRequest(id));
    try {
      const success = await Api.user.delegates.delDelegate(id);

      dispatch(deleteUserDelegateSuccess(id, success));
    } catch (error) {
      dispatch(deleteUserDelegateFailure(id, error));
    }
  };
}

function getUserDelegatesRequest(): UserDelegatesAction {
  return {
    type: ActionType.GET_USER_DELEGATES_REQUEST,
  };
}

function getUserDelegatesSuccess(
  delegates: UserDelegate[]
): UserDelegatesAction {
  return {
    type: ActionType.GET_USER_DELEGATES_SUCCESS,
    delegates,
  };
}

function getUserDelegatesFailure(error: Error): UserDelegatesAction {
  return {
    type: ActionType.GET_USER_DELEGATES_FAILURE,
    error,
  };
}

export function getUserDelegates(): ThunkAction<
  void,
  State,
  unknown,
  UserDelegatesAction
> {
  return async (dispatch, getState) => {
    const { network } = getState();

    if (!network.fetching.user_delegates) {
      dispatch(getUserDelegatesRequest());
      try {
        const delegates = await Api.user.delegates.getDelegates();

        dispatch(
          getUserDelegatesSuccess(
            delegates.map((delegate) => ({
              id: delegate.id,
              email: delegate.delegateEmail,
              active: delegate.isActive,
            }))
          )
        );
      } catch (error) {
        dispatch(getUserDelegatesFailure(error));
      }
    }
  };
}

function createUserDelegateRequest(
  payload: Pick<UserDelegate, "email" | "active">
): UserDelegatesAction {
  return {
    type: ActionType.CREATE_USER_DELEGATE_REQUEST,
    payload,
  };
}

function createUserDelegateSuccess(
  delegate: UserDelegate
): UserDelegatesAction {
  return {
    type: ActionType.CREATE_USER_DELEGATE_SUCCESS,
    delegate,
  };
}

function createUserDelegateFailure(error: Error): UserDelegatesAction {
  return {
    type: ActionType.CREATE_USER_DELEGATE_FAILURE,
    error,
  };
}

export function createUserDelegate(
  payload: Pick<UserDelegate, "email" | "active">
): ThunkAction<void, State, unknown, UserDelegatesAction> {
  return async (dispatch) => {
    dispatch(createUserDelegateRequest(payload));
    try {
      const {
        id,
        delegateEmail,
        isActive,
      } = await Api.user.delegates.postDelegate({
        delegateEmail: payload.email,
        isActive: payload.active,
      });

      dispatch(
        createUserDelegateSuccess({
          id,
          email: delegateEmail,
          active: isActive,
        })
      );
    } catch (error) {
      dispatch(createUserDelegateFailure(error));
    }
  };
}

function updateUserDelegateRequest(payload: UserDelegate): UserDelegatesAction {
  return {
    type: ActionType.UPDATE_USER_DELEGATE_REQUEST,
    payload,
  };
}

function updateUserDelegateSuccess(
  delegate: UserDelegate
): UserDelegatesAction {
  return {
    type: ActionType.UPDATE_USER_DELEGATE_SUCCESS,
    delegate,
  };
}

function updateUserDelegateFailure(
  id: UserDelegate["id"],
  error: Error
): UserDelegatesAction {
  return {
    type: ActionType.UPDATE_USER_DELEGATE_FAILURE,
    id,
    error,
  };
}

export function updateUserDelegate(
  payload: UserDelegate
): ThunkAction<Promise<any>, State, unknown, UserDelegatesAction> {
  return async (dispatch) => {
    dispatch(updateUserDelegateRequest(payload));
    try {
      const {
        id,
        delegateEmail,
        isActive,
      } = await Api.user.delegates.putDelegate({
        id: payload.id,
        delegateEmail: payload.email,
        isActive: payload.active,
      });

      return dispatch(
        updateUserDelegateSuccess({
          id,
          email: delegateEmail,
          active: isActive,
        })
      );
    } catch (error) {
      return dispatch(updateUserDelegateFailure(payload.id, error));
    }
  };
}
